import { Box, Breadcrumbs, IconButton, Link, Menu, MenuItem, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HomeIcon from "@mui/icons-material/Home";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import React, { useState } from "react";

import InlineNameField, { type InlineNameEdit } from "./InlineNameField";
import { type FolderIndex, type FolderKey, folderPath } from "./folderTree";
import { LISTING_TOUR_ANCHOR } from "./listingTour";

export interface FolderActions {
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  /** Set, replace or remove the folder's boundary layer. */
  onBoundary: () => void;
  hasBoundary: boolean;
}

interface FolderBreadcrumbsProps {
  index: FolderIndex;
  openFolderId: FolderKey;
  onOpen: (id: FolderKey) => void;
  /** Drop handlers for a crumb (drag-and-drop filing); null when nothing is being dragged. */
  dropPropsFor?: (target: FolderKey, key: string) => React.HTMLAttributes<HTMLElement>;
  dropTargetKey?: string | null;
  /** Rename / move / delete for the open folder; omitted at root or for view-only users. */
  actions?: FolderActions | null;
  /** Set while the open folder's name is being edited in place. */
  rename?: InlineNameEdit | null;
  /** The Archive is open: "All projects / Archive". */
  archive?: boolean;
}

const crumbKey = (id: FolderKey) => (id === null ? "crumb:root" : `crumb:${id}`);

/** "All projects / Region / Coastal" with the open folder's actions on the end. */
export default function FolderBreadcrumbs({
  index,
  openFolderId,
  onOpen,
  dropPropsFor,
  dropTargetKey,
  actions,
  rename,
  archive = false,
}: FolderBreadcrumbsProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const path = archive ? [] : folderPath(index, openFolderId);
  const current = path[path.length - 1];

  const dropStyle = (key: string) =>
    dropTargetKey === key
      ? { outline: "2px solid #00d4ad", outlineOffset: 2, borderRadius: 1, backgroundColor: "rgba(0,212,173,0.12)" }
      : undefined;

  const crumb = (id: FolderKey, label: React.ReactNode, isLast: boolean) => {
    const key = crumbKey(id);
    const dropProps = dropPropsFor ? dropPropsFor(id, key) : {};
    if (isLast) {
      return (
        <Typography
          key={key}
          component="span"
          sx={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.5, ...dropStyle(key) }}
          {...dropProps}
        >
          {label}
        </Typography>
      );
    }
    return (
      <Link
        key={key}
        component="button"
        type="button"
        underline="hover"
        color="inherit"
        onClick={() => onOpen(id)}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.5, font: "inherit", ...dropStyle(key) }}
        {...dropProps}
      >
        {label}
      </Link>
    );
  };

  return (
    <Box
      data-tour={LISTING_TOUR_ANCHOR.breadcrumbs}
      sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}
    >
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ minWidth: 0 }}>
        {crumb(
          null,
          <>
            <HomeIcon fontSize="small" />
            All projects
          </>,
          path.length === 0 && !archive
        )}
        {archive && (
          <Typography
            key="crumb:archive"
            component="span"
            sx={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.5 }}
          >
            <Inventory2OutlinedIcon fontSize="small" />
            Archive
          </Typography>
        )}
        {path.map((folder, i) =>
          crumb(
            folder.id,
            rename && folder === current ? (
              <InlineNameField
                initialName={folder.name}
                ariaLabel="Folder name"
                messages="tooltip"
                sx={{ width: 240 }}
                {...rename}
              />
            ) : (
              folder.name
            ),
            i === path.length - 1
          )
        )}
      </Breadcrumbs>
      {current && actions && (
        <>
          <IconButton size="small" aria-label="Folder actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                actions.onRename();
              }}
            >
              <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Rename folder
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                actions.onMove();
              }}
            >
              <DriveFileMoveOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Move folder…
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                actions.onBoundary();
              }}
            >
              <PentagonOutlinedIcon fontSize="small" sx={{ mr: 1 }} />{" "}
              {actions.hasBoundary ? "Folder boundary…" : "Add folder boundary…"}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                actions.onDelete();
              }}
            >
              <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete folder
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
}
